import { Box, Button, PopoverPosition, Typography, alpha, useTheme } from "@mui/material";
import { DataObjectOutlined } from "@mui/icons-material";
import { useEffect, useMemo, useRef, useState } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import VariableMenu from "./variableMenu";
import { EmailVariable } from "./types";
import {
  bodyToEditorHtml,
  editorHtmlToBody,
  editorHtmlToSubject,
  registerVariableBlot,
  subjectToEditorHtml,
} from "./template";

registerVariableBlot();

const bodyToolbar = [
  [{ header: [1, 2, 3, false] }],
  ["bold", "italic", "underline", "strike", "blockquote"],
  [{ list: "ordered" }, { list: "bullet" }, { indent: "-1" }, { indent: "+1" }],
  [{ align: [] }, { color: [] }, { background: [] }],
  ["link", "clean"],
];

const bodyFormats = [
  "header",
  "bold",
  "italic",
  "underline",
  "strike",
  "blockquote",
  "list",
  "bullet",
  "indent",
  "link",
  "image",
  "align",
  "color",
  "background",
  "variable",
];

const subjectFormats = ["variable"];

interface TemplateEditorProps {
  variant: "subject" | "body";
  label: string;
  value: string;
  onChange: (template: string) => void;
  variables: EmailVariable[];
  placeholder?: string;
  helper?: React.ReactNode;
  error?: boolean;
}

export default function TemplateEditor({
  variant,
  label,
  value,
  onChange,
  variables,
  placeholder,
  helper,
  error,
}: TemplateEditorProps) {
  const theme = useTheme();
  const quillRef = useRef<ReactQuill>(null);
  const lastEmitted = useRef<string | null>(null);
  const triggerIndex = useRef<number | null>(null);
  const savedIndex = useRef<number | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [menuPosition, setMenuPosition] = useState<PopoverPosition | null>(null);
  const isSubject = variant === "subject";

  const modules = useMemo(
    () =>
      isSubject
        ? {
            toolbar: false,
            keyboard: {
              bindings: {
                noEnter: { key: 13, shiftKey: null, handler: () => false },
              },
            },
          }
        : { toolbar: bodyToolbar },
    [isSubject],
  );

  useEffect(() => {
    const editor = quillRef.current?.getEditor();
    if (!editor || !variables.length || value === lastEmitted.current) return;
    const html = isSubject
      ? subjectToEditorHtml(value, variables)
      : bodyToEditorHtml(value, variables);
    editor.setContents(editor.clipboard.convert(html), "silent");
    lastEmitted.current = value;
  }, [value, variables, isSubject]);

  const openAtCursor = (index: number) => {
    const editor = quillRef.current?.getEditor();
    if (!editor) return;
    const bounds = editor.getBounds(index);
    const rect = (editor.root.parentElement ?? editor.root).getBoundingClientRect();
    triggerIndex.current = index;
    setMenuPosition({ left: rect.left + bounds.left, top: rect.top + bounds.bottom + 4 });
  };

  const handleChange = (content: string, delta: any, source: string) => {
    if (source !== "user") return;
    const template = isSubject ? editorHtmlToSubject(content) : editorHtmlToBody(content);
    lastEmitted.current = template;
    onChange(template);
    const typedBrace = delta?.ops?.some(
      (op: any) => typeof op.insert === "string" && op.insert.endsWith("{"),
    );
    if (!typedBrace) return;
    const editor = quillRef.current?.getEditor();
    const range = editor?.getSelection();
    if (editor && range && range.index >= 2 && editor.getText(range.index - 2, 2) === "{{") {
      openAtCursor(range.index);
    }
  };

  const closeMenu = () => {
    setMenuAnchor(null);
    setMenuPosition(null);
    triggerIndex.current = null;
  };

  const insertVariable = (variable: EmailVariable) => {
    const editor = quillRef.current?.getEditor();
    if (!editor) return;
    let index = Math.min(savedIndex.current ?? Infinity, editor.getLength() - 1);
    if (triggerIndex.current !== null) {
      index = triggerIndex.current - 2;
      editor.deleteText(index, 2, "user");
    }
    editor.insertEmbed(index, "variable", { token: variable.token, label: variable.label }, "user");
    closeMenu();
    setTimeout(() => editor.setSelection(index + 1, 0, "silent"));
  };

  const pillSx = {
    "& .ql-variable": {
      display: "inline-block",
      px: 0.75,
      mx: 0.25,
      borderRadius: "6px",
      backgroundColor: alpha(theme.palette.primary.main, 0.14),
      color: theme.palette.primary.main,
      fontWeight: 500,
      fontSize: "0.92em",
      lineHeight: 1.6,
      userSelect: "all",
      whiteSpace: "nowrap",
    },
  };

  const iconColors = {
    "& .ql-snow .ql-stroke": { stroke: theme.palette.text.secondary },
    "& .ql-snow .ql-fill": { fill: theme.palette.text.secondary },
    "& .ql-snow .ql-picker": { color: theme.palette.text.secondary },
    "& .ql-snow button:hover .ql-stroke, & .ql-snow .ql-active .ql-stroke": {
      stroke: theme.palette.primary.main,
    },
    "& .ql-snow button:hover .ql-fill, & .ql-snow .ql-active .ql-fill": {
      fill: theme.palette.primary.main,
    },
    "& .ql-snow .ql-picker-options": {
      backgroundColor: theme.palette.background.paper,
      borderColor: theme.palette.stroke,
      borderRadius: 1,
    },
    "& .ql-snow .ql-tooltip": {
      backgroundColor: theme.palette.background.paper,
      color: theme.palette.text.primary,
      borderColor: theme.palette.stroke,
      boxShadow: theme.shadows[3],
      zIndex: 2,
    },
  };

  const editorSx = isSubject
    ? {
        "& .ql-container.ql-snow": {
          border: "1px solid transparent",
          borderColor: error ? theme.palette.error.main : "transparent",
          borderRadius: "8px",
          backgroundColor: theme.palette.background.textField,
          fontFamily: "inherit",
          fontSize: 14,
          transition: "border-color .15s",
        },
        "&:focus-within .ql-container.ql-snow": {
          borderColor: error ? theme.palette.error.main : theme.palette.primary.main,
        },
        "& .ql-editor": { padding: "8.5px 14px", minHeight: 40, lineHeight: 1.6 },
      }
    : {
        border: `1px solid ${error ? theme.palette.error.main : theme.palette.stroke}`,
        borderRadius: "8px",
        overflow: "visible",
        transition: "border-color .15s",
        "&:focus-within": {
          borderColor: error ? theme.palette.error.main : theme.palette.primary.main,
        },
        "& .ql-toolbar.ql-snow": {
          border: "none",
          borderBottom: `1px solid ${theme.palette.stroke}`,
          borderRadius: "8px 8px 0 0",
          backgroundColor: theme.palette.background.textField,
        },
        "& .ql-container.ql-snow": {
          border: "none",
          fontFamily: "inherit",
          fontSize: 14,
        },
        "& .ql-editor": { minHeight: 200, lineHeight: 1.6 },
      };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Typography sx={{ fontSize: "1rem", fontWeight: 500 }}>{label}</Typography>
        <Button
          ref={buttonRef}
          size="small"
          variant={"smoth" as any}
          startIcon={<DataObjectOutlined fontSize="small" />}
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => {
            triggerIndex.current = null;
            savedIndex.current = quillRef.current?.getEditor().getSelection()?.index ?? null;
            setMenuAnchor(buttonRef.current);
          }}
          disabled={!variables.length}
        >
          Inserir informação
        </Button>
      </Box>
      <Box
        sx={{
          ...editorSx,
          ...pillSx,
          ...iconColors,
          "& .ql-editor": {
            ...(editorSx as any)["& .ql-editor"],
            color: theme.palette.text.primary,
          },
          "& .ql-editor.ql-blank::before": {
            color: theme.palette.text.disabled,
            fontStyle: "normal",
            left: isSubject ? 14 : 15,
          },
        }}
      >
        <ReactQuill
          ref={quillRef}
          theme="snow"
          modules={modules}
          formats={isSubject ? subjectFormats : bodyFormats}
          placeholder={placeholder}
          onChange={handleChange}
        />
      </Box>
      {helper}
      <VariableMenu
        variables={variables}
        anchorEl={menuAnchor}
        anchorPosition={menuPosition}
        onClose={closeMenu}
        onSelect={insertVariable}
      />
    </Box>
  );
}
